import { PlainDate } from "@sa-nu/shared-utils/date";

export type DatePickerSelection = {
  startDate: PlainDate | null;
  endDate: PlainDate | null;
};

/**
 * clicked/date が endDate として選択可能な候補かを判定する。
 * 描画 (deriveDateCellState) とクリック処理 (getNextSelection) が同じ述語を共有することで、
 * 「見た目は選べそうなのにクリックすると拒否される」乖離を防ぐ。
 */
export function isValidEndDateCandidate(params: {
  startDate: PlainDate;
  candidate: PlainDate;
  candidateDisabled: boolean;
  maxNights: number;
}): boolean {
  const { startDate, candidate, candidateDisabled, maxNights } = params;
  return (
    !candidateDisabled &&
    PlainDate.compare(candidate, startDate) > 0 &&
    PlainDate.compare(candidate, startDate.add({ days: maxNights })) <= 0
  );
}

/**
 * 外部から渡された selection を正規化する。
 * 不正な組み合わせ (endDate のみ非 null / endDate <= startDate) は endDate を無視する。
 * 描画 (deriveDateCellState) とクリック処理 (getNextSelection) の両方に同じ正規化結果を
 * 渡すことで、見た目と挙動の乖離を防ぐ。
 */
export function normalizeSelection(
  startDate: PlainDate | null,
  endDate: PlainDate | null,
): DatePickerSelection {
  const isValidRange =
    startDate !== null &&
    endDate !== null &&
    PlainDate.compare(startDate, endDate) < 0;
  return { startDate, endDate: isValidRange ? endDate : null };
}

/**
 * 日付クリックによる選択状態の遷移を計算する。
 * フェーズは current から導出する: startDate なし = idle / startDate のみ = selectingEnd /
 * 両方あり = confirmed。maxNights 未指定の場合は単一選択モード (常に startDate を更新)。
 */
export function getNextSelection(params: {
  current: DatePickerSelection;
  clicked: PlainDate;
  clickedDisabled: boolean;
  maxNights: number | undefined;
}): DatePickerSelection {
  const { current, clicked, clickedDisabled, maxNights } = params;

  // 単一選択モード
  if (maxNights === undefined) {
    return { startDate: clicked, endDate: null };
  }

  switch (derivePhase(current)) {
    case "idle":
      return { startDate: clicked, endDate: null };

    case "selectingEnd": {
      const { startDate } = current;
      if (startDate === null) return { startDate: null, endDate: null };

      if (
        isValidEndDateCandidate({
          startDate,
          candidate: clicked,
          candidateDisabled: clickedDisabled,
          maxNights,
        })
      ) {
        return { startDate, endDate: clicked };
      }
      // 窓外・startDate 自身/以前・disabled → 選択キャンセル
      return { startDate: null, endDate: null };
    }

    case "confirmed":
      // 新規選択開始。ただし disabled な日 (帯が disabled 日を跨ぐ場合の中間日など) は
      // 新しい startDate にできないためクリア扱いにする
      if (clickedDisabled) {
        return { startDate: null, endDate: null };
      }
      return { startDate: clicked, endDate: null };
  }
}

type Phase = "idle" | "selectingEnd" | "confirmed";

function derivePhase(selection: DatePickerSelection): Phase {
  if (selection.startDate === null) return "idle";
  if (selection.endDate === null) return "selectingEnd";
  return "confirmed";
}
